/**
 * Calendario API Queries
 * Centralized data fetching for Calendario feature
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  CalendarioEvento, 
  StrutturaCalendario,
  QueryOptions 
} from '@/types';

const TABLE = 'prenotazioni';

// ============================================================
// GET Queries
// ============================================================

export async function getCalendarioEventi(options?: { 
  userId?: string; 
  mese?: number; 
  anno?: number; 
  strutturaId?: string;
}) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*, strutture(nome)')
    .eq('user_id', userId);

  // Filtra per mese e anno se specificati
  if (options?.mese !== undefined && options?.anno !== undefined) {
    const primoGiornoMese = new Date(options.anno, options.mese, 1).toISOString();
    const ultimoGiornoMese = new Date(options.anno, options.mese + 1, 0).toISOString();
    
    query = query
      .gte('data_checkin', primoGiornoMese)
      .lte('data_checkin', ultimoGiornoMese);
  }

  // Filtra per struttura se specificata
  if (options?.strutturaId) {
    query = query.eq('struttura_id', options.strutturaId);
  }

  // Ordina per data di check-in
  query = query.order('data_checkin', { ascending: true });

  const { data, error } = await query;

  return {
    data: data as (CalendarioEvento & { 
      strutture: { nome: string } | null 
    })[] | null,
    error,
  };
}

export async function getStruttureCalendario(options?: { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from('strutture')
    .select('*')
    .eq('user_id', userId)
    .eq('attiva', true)
    .order('nome', { ascending: true });

  return {
    data: data as StrutturaCalendario[] | null,
    error,
  };
}

// ============================================================
// UTILITIES
// ============================================================

export async function getEventiPerPeriodo(options: {
  userId?: string;
  dataInizio: string;
  dataFine: string;
  strutturaId?: string;
}) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*, strutture(nome)')
    .eq('user_id', userId)
    .gte('data_checkin', options.dataInizio)
    .lte('data_checkout', options.dataFine);

  if (options.strutturaId) {
    query = query.eq('struttura_id', options.strutturaId);
  }

  const { data, error } = await query.order('data_checkin', { ascending: true });

  return {
    data: data as (CalendarioEvento & { 
      strutture: { nome: string } | null 
    })[] | null,
    error,
  };
}

export async function getDisponibilitaStruttura(options: {
  strutturaId: string;
  dataInizio: string;
  dataFine: string;
  userId?: string;
}) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  // Ottieni le camere della struttura
  const { data: camere, error: errorCamere } = await supabase
    .from('camere')
    .select('*')
    .eq('struttura_id', options.strutturaId)
    .eq('user_id', userId);

  if (errorCamere) throw errorCamere;

  // Ottieni le prenotazioni nel periodo
  const { data: prenotazioni, error: errorPrenotazioni } = await supabase
    .from(TABLE)
    .select('*')
    .eq('struttura_id', options.strutturaId)
    .eq('user_id', userId)
    .or(`data_checkin.lte.${options.dataFine},data_checkout.gte.${options.dataInizio}`)
    .in('stato', ['Confermata', 'In corso', 'Check-in online effettuato']);

  if (errorPrenotazioni) throw errorPrenotazioni;

  return {
    camere: camere || [],
    prenotazioni: prenotazioni || [],
    disponibili: (camere?.length || 0) - (prenotazioni?.length || 0),
  };
}
